// pages/company/Dashboard.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  PlusCircle,
  Eye,
  Calendar,
  TrendingUp,
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  AlertTriangle,
  HelpCircle,
  LogOut,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useCompanyAuth } from '@/contexts/CompanyAuthContext';
import { supabase } from '@/lib/supabaseClient';
import { demoCompanyService, DemoSubmission } from '@/lib/demoCompanyService';

export default function CompanyDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated, isLoading: authLoading, logout } = useCompanyAuth();
  const [submissions, setSubmissions] = useState<DemoSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Real-time connection status
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Refs for subscriptions
  const subscriptionsRef = useRef<any[]>([]);

  // Load company data function
  const loadCompanyData = useCallback(() => {
    setIsLoading(true);
    
    if (user) {
      // Try to find demo submissions for this company email
      const allSubmissions = demoCompanyService.getAllSubmissions();
      // Find company by email match
      const allCompanies = demoCompanyService.getAllCompanies();
      const matchedCompany = allCompanies.find(c => c.email === user.email);
      
      if (matchedCompany) {
        const companySubmissions = allSubmissions.filter(s => s.companyId === matchedCompany.id);
        setSubmissions(companySubmissions);
      } else {
        setSubmissions([]);
      }
    }
    
    setLastUpdated(new Date());
    setIsLoading(false);
  }, [user]);

  // Load from Supabase if not using demo
  const loadSupabaseData = useCallback(async () => {
    if (!user || !user.id) return;
    
    setIsLoading(true);
    try {
      // Load submissions from Supabase for this company
      const { data, error } = await supabase
        .from('external_submissions')
        .select('*')
        .eq('company_id', user.id)
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      
      // Map Supabase data to match DemoSubmission format
      const mappedSubmissions = (data || []).map(sub => ({
        id: sub.id,
        companyId: sub.company_id,
        problemTitle: sub.problem_title,
        description: sub.problem_description,
        industry: sub.industry_sector,
        urgency: sub.urgency || 'medium',
        status: sub.status || 'pending',
        estimatedImpact: sub.estimated_impact,
        submittedAt: sub.submitted_at,
        updatedAt: sub.updated_at
      }));
      
      setSubmissions(mappedSubmissions);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading submissions from Supabase:', error);
      // Fallback to demo data
      loadCompanyData();
    } finally {
      setIsLoading(false);
    }
  }, [user, loadCompanyData]);

  // Refresh function
  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await loadSupabaseData();
      toast({
        title: "Data Refreshed",
        description: "Your submissions have been updated.",
      });
    } catch (error) {
      console.error('Refresh failed:', error);
      toast({
        title: "Refresh Failed",
        description: "Could not refresh data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [loadSupabaseData, toast]);

  // Setup real-time subscriptions
  useEffect(() => {
    // Check authentication using the auth context
    if (!authLoading && !isAuthenticated) {
      navigate('/company/login');
      return;
    }

    if (isAuthenticated && user) {
      // Load initial data
      loadSupabaseData();

      // Setup real-time subscription for this company's submissions
      const setupSubscriptions = () => {
        // Clean up existing subscriptions
        subscriptionsRef.current.forEach(sub => {
          try {
            sub.unsubscribe();
          } catch (e) {
            console.warn('Error unsubscribing:', e);
          }
        });
        subscriptionsRef.current = [];

        // Only setup real-time if we have a real user ID (not demo)
        if (user.id && !user.email?.includes('demo')) {
          const submissionsSub = supabase
            .channel('company_submissions_changes')
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'external_submissions',
                filter: `company_id=eq.${user.id}`
              },
              (payload) => {
                console.log('Company submission changed:', payload.eventType);
                // Debounce the refresh
                setTimeout(() => loadSupabaseData(), 100);
              }
            )
            .subscribe((status) => {
              console.log('Submissions subscription status:', status);
              if (status === 'SUBSCRIBED') {
                setIsRealtimeConnected(true);
              } else if (status === 'CHANNEL_ERROR') {
                setIsRealtimeConnected(false);
              }
            });

          subscriptionsRef.current = [submissionsSub];
        } else {
          // For demo users, use polling instead
          setIsRealtimeConnected(false);
        }
      };

      setupSubscriptions();

      // Polling fallback (every 30 seconds for demo users or when real-time fails)
      const intervalId = setInterval(() => {
        if (!isRealtimeConnected || user.email?.includes('demo')) {
          console.log('Polling for updates...');
          loadSupabaseData();
        }
      }, 30000);

      // Cleanup on unmount
      return () => {
        clearInterval(intervalId);
        subscriptionsRef.current.forEach(sub => {
          try {
            sub.unsubscribe();
          } catch (e) {
            console.warn('Error unsubscribing:', e);
          }
        });
      };
    }
  }, [isAuthenticated, user, authLoading, navigate, loadSupabaseData, isRealtimeConnected]);

  const handleLogout = async () => {
    await logout();
    toast({
      title: 'Logged out',
      description: 'You have been logged out successfully',
    });
    navigate('/company/login');
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'under_review': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'accepted': return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'under_review': return <Eye className="h-4 w-4" />;
      case 'accepted': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const stats = {
    total: submissions.length,
    pending: submissions.filter(s => s.status === 'pending').length,
    underReview: submissions.filter(s => s.status === 'under_review').length,
    accepted: submissions.filter(s => s.status === 'accepted').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
    acceptanceRate: submissions.length > 0 
      ? Math.round((submissions.filter(s => s.status === 'accepted').length / submissions.length) * 100)
      : 0
  };

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card>
          <CardContent className="pt-6 pb-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-lg font-medium">Loading company data...</p>
            <p className="text-sm text-muted-foreground mt-2">Please wait</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card>
          <CardContent className="pt-6 pb-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <p className="text-lg font-medium">Company not found</p>
            <p className="text-sm text-muted-foreground mt-2">Please log in again</p>
            <Button 
              onClick={() => navigate('/company/login')} 
              className="mt-4"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Company Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {user.contactName || user.name}. Track your problem submissions here.
          </p>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-xs text-muted-foreground">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
            <div className="flex items-center gap-1">
              {isRealtimeConnected ? (
                <>
                  <Wifi className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3 text-red-500" />
                  <span className="text-xs text-red-600">Polling</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={refreshData} 
            disabled={isRefreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          
          <Button 
            onClick={() => navigate('/company/submit')}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 gap-2"
          >
            <PlusCircle className="h-4 w-4" />
            Submit New Problem
          </Button>
          
          <Button variant="outline" onClick={handleLogout} className="gap-2">
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total</p>
                <p className="text-3xl font-bold mt-2">{stats.total}</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-full text-white">
                <FileText className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                <p className="text-3xl font-bold mt-2 text-yellow-600">{stats.pending}</p>
              </div>
              <div className="bg-yellow-500 p-3 rounded-full text-white">
                <Clock className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Under Review</p>
                <p className="text-3xl font-bold mt-2 text-blue-600">{stats.underReview}</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-full text-white">
                <Eye className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Accepted</p>
                <p className="text-3xl font-bold mt-2 text-green-600">{stats.accepted}</p>
              </div>
              <div className="bg-green-500 p-3 rounded-full text-white">
                <CheckCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Rejected</p>
                <p className="text-3xl font-bold mt-2 text-red-600">{stats.rejected}</p>
              </div>
              <div className="bg-red-500 p-3 rounded-full text-white">
                <XCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Acceptance</p>
                <p className="text-3xl font-bold mt-2 text-emerald-600">{stats.acceptanceRate}%</p>
              </div>
              <div className="bg-emerald-500 p-3 rounded-full text-white">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-700">
            <Building2 className="h-5 w-5" />
            Company Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Company Name</p>
              <p className="text-sm font-medium">{user.companyName}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Industry</p>
              <p className="text-sm font-medium capitalize">{user.industry?.replace('_', ' ') || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Location</p>
              <p className="text-sm font-medium flex items-center gap-1">
                <MapPin className="h-3 w-3 text-muted-foreground" />
                {user.location || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Contact</p>
              <p className="text-sm font-medium">{user.contactName || user.name}</p>
              <p className="text-xs text-muted-foreground">{user.contactTitle || 'Contact'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Email</p>
              <p className="text-sm font-medium flex items-center gap-1">
                <Mail className="h-3 w-3 text-muted-foreground" />
                {user.email}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Phone</p>
              <p className="text-sm font-medium flex items-center gap-1">
                <Phone className="h-3 w-3 text-muted-foreground" />
                {user.phone || 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Submissions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Submissions</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/company/submissions')} className="gap-1">
            View All
            <ArrowUpRight className="h-3 w-3" />
          </Button>
        </CardHeader>
        <CardContent>
          {submissions.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
              <h3 className="text-lg font-medium mb-2">No submissions yet</h3>
              <p className="text-muted-foreground mb-6">
                Submit your first industrial problem for review
              </p>
              <Button onClick={() => navigate('/company/submit')}>
                <PlusCircle className="h-4 w-4 mr-2" />
                Submit a Problem
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.slice(0, 3).map((submission) => (
                <div 
                  key={submission.id}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/company/submissions/${submission.id}`)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className={getStatusColor(submission.status)}>
                        <span className="flex items-center gap-1">
                          {getStatusIcon(submission.status)}
                          {submission.status.replace('_', ' ')}
                        </span>
                      </Badge>
                      <Badge variant="outline" className={
                        submission.urgency === 'high' ? 'bg-red-100 text-red-700' :
                        submission.urgency === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }>
                        {submission.urgency} urgency
                      </Badge>
                    </div>
                    <h3 className="font-medium">{submission.problemTitle}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {submission.description}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(submission.submittedAt).toLocaleDateString()}
                      </span>
                      {submission.estimatedImpact && (
                        <span className="text-emerald-600 font-medium">
                          {submission.estimatedImpact}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/company/submit')}
            >
              <PlusCircle className="h-6 w-6 text-emerald-600" />
              <span>New Problem</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/company/submissions')}
            >
              <FileText className="h-6 w-6 text-blue-600" />
              <span>View All</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/company/profile')}
            >
              <Building2 className="h-6 w-6 text-purple-600" />
              <span>Profile</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/company/support')}
            >
              <HelpCircle className="h-6 w-6 text-orange-600" />
              <span>Support</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}